import { Link } from 'react-router-dom';
import { CustomerNav } from '../components/CustomerNav';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Utensils, SprayCan, Code } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const services = [
  {
    id: 'spa',
    titleKey: 'ftasmSpa',
    description: 'Luxury spa treatments and relaxation therapy',
    descriptionAr: 'علاجات سبا فاخرة وعلاج استرخاء',
    descriptionHi: 'लक्जरी स्पा उपचार और विश्राम चिकित्सा',
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1691662754730-2415e37e1052?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBtYXNzYWdlJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc2NjQ3NzkzM3ww&ixlib=rb-4.1.0&q=85',
    color: 'from-terracotta/20 to-golden-hour/20'
  },
  {
    id: 'kitchen',
    titleKey: 'ftasmKitchen',
    description: 'Michelin Star fine dining experience',
    descriptionAr: 'تجربة طعام فاخر حاصلة على نجمة ميشلان',
    descriptionHi: 'मिशेलिन स्टार फाइन डाइनिंग अनुभव',
    icon: Utensils,
    image: 'https://images.unsplash.com/photo-1707313829962-28409766bd7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtaWNoZWxpbiUyMHN0YXIlMjBwbGF0ZWQlMjBmb29kJTIwZmluZSUyMGRpbmluZ3xlbnwwfHx8fDE3NjY0Nzc5MzR8MA&ixlib=rb-4.1.0&q=85',
    color: 'from-sage/20 to-terracotta/10'
  },
  {
    id: 'cleaning',
    titleKey: 'ftasmCleaning',
    description: 'Professional cleaning services',
    descriptionAr: 'خدمات تنظيف احترافية',
    descriptionHi: 'पेशेवर सफाई सेवाएं',
    icon: SprayCan,
    image: 'https://images.unsplash.com/photo-1758273238400-9b1367ae90ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBob21lJTIwY2xlYW5pbmclMjBzZXJ2aWNlJTIwYnJpZ2h0fGVufDB8fHx8MTc2NjQ3NzkzN3ww&ixlib=rb-4.1.0&q=85',
    color: 'from-sage/30 to-warm-sand'
  },
  {
    id: 'coding',
    titleKey: 'ftasmCoding',
    description: 'Tech workshops from FJT & FTASM stars',
    descriptionAr: 'ورش عمل تقنية من نجوم FJT و FTASM',
    descriptionHi: 'FJT और FTASM सितारों से तकनीकी कार्यशालाएं',
    icon: Code,
    image: 'https://images.unsplash.com/photo-1565843708714-52ecf69ab81f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwcm9ncmFtbWVyJTIwY29kaW5nJTIwbGFwdG9wJTIwY29mZmVlJTIwYWVzdGhldGljfGVufDB8fHx8MTc2NjQ3NzkzOHww&ixlib=rb-4.1.0&q=85',
    color: 'from-deep-charcoal/10 to-sage/20'
  }
];

const branches = [
  { name: 'Al Markhiya Plaza Villa 47', city: 'Doha, Qatar', cityAr: 'الدوحة، قطر', cityHi: 'दोहा, कतर' },
  { name: 'Ghanem Gardens Al Shooei Villa 84', city: 'Doha, Qatar', cityAr: 'الدوحة، قطر', cityHi: 'दोहा, कतर' },
  { name: 'Al Qusais Dubai Sajaya 2 Villa 803', city: 'Dubai, UAE', cityAr: 'دبي، الإمارات', cityHi: 'दुबई, यूएई' },
  { name: 'Kodavoor Selma Manzil', city: 'India', cityAr: 'الهند', cityHi: 'भारत' },
  { name: 'Shoba Gardens Villa 2 Tareens', city: 'Mysuru, India', cityAr: 'ميسورو، الهند', cityHi: 'मैसूर, भारत' },
  { name: 'Karama', city: 'Dubai, UAE', cityAr: 'دبي، الإمارات', cityHi: 'दुबई, यूएई' },
];

export default function Landing() {
  const { t, language, isRTL } = useLanguage();

  const getServiceDescription = (service) => {
    if (language === 'ar') return service.descriptionAr;
    if (language === 'hi') return service.descriptionHi;
    return service.description;
  };

  const getBranchCity = (branch) => {
    if (language === 'ar') return branch.cityAr;
    if (language === 'hi') return branch.cityHi;
    return branch.city;
  };

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <CustomerNav />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-deep-charcoal tracking-tight leading-tight">
                {t('heroTitle')} <br />
                <span className="text-terracotta">{t('heroPremium')}</span> {t('heroServices')}
              </h1>
              <p className="font-body text-lg text-deep-charcoal/70 max-w-lg">
                {t('heroSubtitle')}
              </p>
              <div className={`flex flex-wrap gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/booking">
                  <Button 
                    className="rounded-full px-8 py-6 bg-terracotta text-white hover:bg-terracotta-dark shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="hero-book-now"
                  >
                    {t('bookNow')}
                    <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    className="rounded-full px-8 py-6 border-deep-charcoal/20 text-deep-charcoal hover:bg-warm-sand"
                    data-testid="hero-explore"
                  >
                    {t('exploreServices')}
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Image Grid */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg animate-fade-in-up stagger-1">
                    <img 
                      src={services[0].image} 
                      alt="Spa" 
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg animate-fade-in-up stagger-3">
                    <img 
                      src={services[2].image} 
                      alt="Cleaning" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg animate-fade-in-up stagger-2">
                    <img 
                      src={services[1].image} 
                      alt="Kitchen" 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-soft-lg animate-fade-in-up stagger-4">
                    <img 
                      src={services[3].image} 
                      alt="Coding" 
                      className="w-full h-48 object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-deep-charcoal mb-4">
              {t('ourServices')}
            </h2>
            <p className="font-body text-lg text-deep-charcoal/60 max-w-2xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={`/services?category=${service.id}`}
                  data-testid={`service-card-${service.id}`}
                  className={`group relative overflow-hidden rounded-3xl bg-gradient-to-br ${service.color} p-8 shadow-soft hover:shadow-soft-lg transition-all duration-500 hover:-translate-y-1 animate-fade-in-up stagger-${index + 1}`}
                >
                  <div className="relative z-10 flex flex-col h-full">
                    <div className={`flex items-center space-x-4 mb-6 ${isRTL ? 'space-x-reverse' : ''}`}>
                      <div className="w-14 h-14 rounded-2xl bg-white/80 flex items-center justify-center shadow-soft">
                        <Icon className="w-7 h-7 text-terracotta" />
                      </div>
                      <h3 className="font-heading text-2xl font-semibold text-deep-charcoal">
                        {t(service.titleKey)}
                      </h3>
                    </div>
                    <p className="font-body text-deep-charcoal/70 mb-6">
                      {getServiceDescription(service)}
                    </p>
                    <div className={`flex items-center text-terracotta font-body font-medium group-hover:translate-x-2 transition-transform duration-300 ${isRTL ? 'group-hover:-translate-x-2' : ''}`}>
                      <span>{t('learnMore')}</span>
                      <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </div>
                  </div>
                  <div className={`absolute bottom-0 ${isRTL ? 'left-0' : 'right-0'} w-40 h-40 opacity-20`}>
                    <img 
                      src={service.image} 
                      alt="" 
                      className={`w-full h-full object-cover ${isRTL ? 'rounded-tr-3xl' : 'rounded-tl-3xl'}`}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-deep-charcoal mb-4">
              {t('ourLocations')}
            </h2>
            <p className="font-body text-lg text-deep-charcoal/60 max-w-2xl mx-auto">
              {t('locationsSubtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches.map((branch, index) => (
              <div
                key={index}
                data-testid={`branch-card-${index}`}
                className="group bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-deep-charcoal/5"
              >
                <div className="w-12 h-12 rounded-xl bg-sage/20 flex items-center justify-center mb-4 group-hover:bg-sage/30 transition-colors">
                  <span className="font-heading text-xl font-bold text-sage">{index + 1}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-deep-charcoal mb-2">
                  {branch.name}
                </h3>
                <p className="font-body text-sm text-deep-charcoal/60">
                  {getBranchCity(branch)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-deep-charcoal">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('readyToExperience')}
          </h2>
          <p className="font-body text-lg text-white/70 mb-10 max-w-2xl mx-auto">
            {t('ctaSubtitle')}
          </p>
          <Link to="/booking">
            <Button 
              className="rounded-full px-10 py-6 bg-terracotta text-white hover:bg-terracotta-dark shadow-lg hover:shadow-xl"
              data-testid="cta-book-now"
            >
              {t('bookYourAppointment')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-warm-sand border-t border-deep-charcoal/10">
        <div className="max-w-7xl mx-auto">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
              <span className="font-heading text-2xl font-bold text-terracotta">FTASM</span>
              <span className="font-body text-sm text-deep-charcoal/60">{t('services')}</span>
            </div>
            <div className={`flex items-center space-x-6 ${isRTL ? 'space-x-reverse' : ''}`}>
              <Link to="/staff/login" className="font-body text-sm text-deep-charcoal/60 hover:text-terracotta transition-colors" data-testid="footer-staff-login">
                {t('staffPortal')}
              </Link>
            </div>
            <p className="font-body text-sm text-deep-charcoal/50">
              © 2025 FTASM Services. {t('allRightsReserved')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
